import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import OrderNumber from '../Cells/OrderNumber';
import { GridColDefExtended } from '@mui/x-data-grid-pro';

import styles from '../../RenderCell.module.css';

const orderNumberField = 'orderNumber';

/** Sub-values of the Order Number column sortable via hidden companion columns */
const orderNumberSubColumns: SubColumnDef<DispatchLeg>[] = [
  { key: 'type', label: 'Type', valueGetter: (row) => row.jobCategoryTransportationType },
  { key: 'leg', label: 'Leg', valueGetter: (row) => row.legNo }
];

const orderNumberSubLabels: HeaderSubLabel[] = orderNumberSubColumns.map((sub) => ({
  label: sub.label,
  field: subColumnField(orderNumberField, sub.key)
}));

const orderNumberDefinition: GridColDefExtended<DispatchLeg> = {
  field: orderNumberField,
  minWidth: 75,
  width: 112,
  headerName: 'Order Number|||Type|||Leg #|||Type',
  subColumns: orderNumberSubColumns,
  /** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
   * static secondaryText instead of mounting the grid-subscribing sort labels per card */
  renderHeader: (params) => (
    <RenderHeader
      primaryText={'Order Number'}
      subLabels={params.field ? orderNumberSubLabels : undefined}
      secondaryText={'Type, Leg'}
    />
  ),
  renderCell: OrderNumber,
  headerClassName: styles['pinned-column'],
  disableReorder: true,
  /** Every board leads with this column, so the columns panel offers it as a locked, disabled toggle */
  hideable: false,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { jobId, csnNo, jobCategoryTransportationType, legNo, legType } = row;
        return (
          caseInsensitiveIncludes(value, legNo) ||
          caseInsensitiveIncludes(value, legType) ||
          caseInsensitiveIncludes(value, [`${jobId.toString()}/${csnNo.toString().padStart(3, '0')}`, jobCategoryTransportationType])
        );
      });
  },
  valueGetter: (_, row: DispatchLeg) =>
    `${String(row.jobId)}|||${row.jobCategoryTransportationType}|||${String(row.legNo)}|||${String(row.legType)}`,
  sortable: true,
  sortComparator: (v1, v2) => {
    if (typeof v1 === 'string' && typeof v2 === 'string') {
      const v1Split = v1.split('|||');
      const v1OrderNo = v1Split[0];
      const v1LegNo = v1Split[2];

      const v2Split = v2.split('|||');
      const v2OrderNo = v2Split[0];
      const v2LegNo = v2Split[2];

      if (Number(v1OrderNo) > Number(v2OrderNo)) {
        return 1;
      } else if (Number(v1OrderNo) < Number(v2OrderNo)) {
        return -1;
      }

      if (Number(v1LegNo) > Number(v2LegNo)) {
        return 1;
      } else if (Number(v1LegNo) < Number(v2LegNo)) {
        return -1;
      }
    }

    return 0;
  }
};

export default orderNumberDefinition;
